import * as ents from '@attain-apps/entities'
import type * as graphql from '@attain-apps/fe/web/main/lib/graphql'

import * as accountList from './account-list'
import * as dataExport from './data-export'
import * as employeeList from './employee-list'
import * as progressByOrganization from './progress-by-organization'
import * as roomLinks from './room-links'
import * as roomSupportingInformation from './room-supporting-information'
import * as roomsByAccount from './rooms-by-account'
import * as roomsByOccupant from './rooms-by-occupant'
import * as roomsByResponsiblePerson from './rooms-by-responsible-person'
import * as spaceSalaryComparison from './space-salary-comparison'
import * as unassignedSponsoredAccounts from './unassigned-sponsored-accounts'
import * as unassignedSponsoredPeople from './unassigned-sponsored-people'
import * as unclaimedRooms from './unclaimed-rooms'

/**
 * Fills in the field of the input that belongs to each report.
 */
const TO_INPUT: {
  readonly [TId in ents.extra.space.Report.Id]: (
    configuration: ents.extra.space.Report.Configuration<TId>,
  ) => graphql.schema.SpaceReportConfigurationInput
} = {
  [ents.extra.space.Report.Id.AccountList]: configuration => ({
    accountList:
      accountList.transformers.CONFIGURATION_INPUT.getGqlFrom(configuration),
  }),
  [ents.extra.space.Report.Id.DataExport]: configuration => ({
    dataExport:
      dataExport.transformers.CONFIGURATION_INPUT.getGqlFrom(configuration),
  }),
  [ents.extra.space.Report.Id.EmployeeList]: configuration => ({
    employeeList:
      employeeList.transformers.CONFIGURATION_INPUT.getGqlFrom(configuration),
  }),
  [ents.extra.space.Report.Id.RoomLinks]: configuration => ({
    roomLinks:
      roomLinks.transformers.CONFIGURATION_INPUT.getGqlFrom(configuration),
  }),
  [ents.extra.space.Report.Id.RoomSupportingInformation]: configuration => ({
    roomSupportingInformation:
      roomSupportingInformation.transformers.CONFIGURATION_INPUT.getGqlFrom(
        configuration,
      ),
  }),
  [ents.extra.space.Report.Id.RoomsByAccount]: configuration => ({
    roomsByAccount:
      roomsByAccount.transformers.CONFIGURATION_INPUT.getGqlFrom(configuration),
  }),
  [ents.extra.space.Report.Id.RoomsByOccupant]: configuration => ({
    roomsByOccupant:
      roomsByOccupant.transformers.CONFIGURATION_INPUT.getGqlFrom(
        configuration,
      ),
  }),
  [ents.extra.space.Report.Id.RoomsByResponsiblePerson]: configuration => ({
    roomsByResponsiblePerson:
      roomsByResponsiblePerson.transformers.CONFIGURATION_INPUT.getGqlFrom(
        configuration,
      ),
  }),
  [ents.extra.space.Report.Id.SpaceSalaryComparison]: configuration => ({
    spaceSalaryComparison:
      spaceSalaryComparison.transformers.CONFIGURATION_INPUT.getGqlFrom(
        configuration,
      ),
  }),
  [ents.extra.space.Report.Id.UnassignedSponsoredAccounts]: configuration => ({
    unassignedSponsoredAccounts:
      unassignedSponsoredAccounts.transformers.CONFIGURATION_INPUT.getGqlFrom(
        configuration,
      ),
  }),
  [ents.extra.space.Report.Id.UnassignedSponsoredPeople]: configuration => ({
    unassignedSponsoredPeople:
      unassignedSponsoredPeople.transformers.CONFIGURATION_INPUT.getGqlFrom(
        configuration,
      ),
  }),
  [ents.extra.space.Report.Id.ProgressByOrganization]: configuration => ({
    progressByOrganization:
      progressByOrganization.transformers.CONFIGURATION_INPUT.getGqlFrom(
        configuration,
      ),
  }),
  [ents.extra.space.Report.Id.UnclaimedRooms]: configuration => ({
    unclaimedRooms:
      unclaimedRooms.transformers.CONFIGURATION_INPUT.getGqlFrom(configuration),
  }),
}

/**
 * Fills in the configuration of the report being generated.
 *
 * NOTE: GraphQL has no input unions, so each report's configuration is its own
 * optional field on the input, and only the one matching the requested report
 * is filled in
 *
 * @see {@link TO_INPUT}
 */
export function getGqlFrom<TId extends ents.extra.space.Report.Id>(
  id: TId,
  configuration: ents.extra.space.Report.Configuration<TId>,
): graphql.schema.SpaceReportConfigurationInput {
  return TO_INPUT[id](configuration)
}
